import i18n from '../i18n/config';

function tr(t, key, fallback) {
  if (typeof t === 'function') {
    const res = t(key, fallback);
    if (res && res !== key) return res;
  }
  return i18n.t(key, fallback);
}

/**
 * 마지막 대화 미리보기 문구 생성 순수 함수
 * @param {object|null} message - Supabase messages 행 객체
 * @param {string|null} myUserId - 현재 로그인한 내 사용자 ID
 * @param {function} t - i18next t 번역 함수
 * @returns {string} 화면에 출력할 문자열 1개
 */
export function getMessagePreview(message, myUserId, t, options = {}) {
  if (!message || typeof message !== 'object') {
    return typeof t === 'function' ? t('common.preview_no_messages') : '';
  }

  const messageType = message.message_type || 'text';

  // [시스템 메시지 미리보기 처리] - 인물명 의존 제거(§I-7-5)
  if (messageType === 'system') {
    const payload = message.system_payload;
    if (payload && typeof payload === 'object') {
      if (payload.event === 'gift_sent') {
        return tr(t, 'message_preview.gift_sent', '무우를 보냈습니다');
      }
      if (payload.event === 'gift_rejected') {
        return tr(t, 'message_preview.gift_rejected', '무우를 사양했습니다');
      }
      if (payload.event === 'gift_claimed') {
        return tr(t, 'message_preview.gift_received', '무우를 받았습니다');
      }
      if (payload.event === 'gift_expired') {
        return tr(t, 'message_preview.gift_expired', '무우가 기한이 지나 반환되었습니다');
      }
    }
    return '';
  }

  let text = '';
  if (messageType === 'image') {
    text = typeof t === 'function' ? t('common.preview_photo') : '';
  } else if (messageType === 'video') {
    text = typeof t === 'function' ? t('common.preview_video') : '';
  } else if (messageType === 'file') {
    text = typeof t === 'function' ? t('common.preview_file') : '';
  } else {
    // 2026-09-11: 수신자 언어에 맞는 번역 칸을 선택하도록 보강
    // (a) options.preferOriginal 이 true 이면 항상 original_text 사용
    if (options.preferOriginal) {
      text = message.original_text || '';
    } else if (myUserId && message.sender_id === myUserId) {
      // (b) 내가 보낸 메시지는 원문 우선 (기존 동작 유지)
      text = message.original_text || message.translated_text || '';
    } else {
      // (c) 상대방이 보낸 메시지: 내 언어(options.myLanguage)와 일치하는 번역문 우선 선택
      const myLang = String(options.myLanguage || '').trim().toLowerCase();
      const targetLang1 = String(message.target_language || '').trim().toLowerCase();
      const targetLang2 = String(message.target_language_2 || '').trim().toLowerCase();

      if (myLang && targetLang1 && myLang === targetLang1 && message.translated_text) {
        text = message.translated_text;
      } else if (myLang && targetLang2 && myLang === targetLang2 && message.translated_text_2) {
        text = message.translated_text_2;
      } else {
        // 일치하는 칸이 없으면 translated_text -> original_text 순으로 폴백
        text = message.translated_text || message.original_text || '';
      }
    }
  }

  // 줄바꿈(\n, \r) 및 연속 공백을 공백 1칸으로 치환
  text = text ? text.replace(/[\r\n\s]+/g, ' ').trim() : '';

  if (!text) {
    return typeof t === 'function' ? t('common.preview_no_messages') : '';
  }

  // 내가 보낸 메시지인 경우 "나: " (t('message_preview.sender_me')) 접두어 추가
  if (myUserId && message.sender_id === myUserId) {
    const mePrefix = tr(t, 'message_preview.sender_me', '나: ');
    return `${mePrefix}${text}`.replace(/[\r\n\s]+/g, ' ').trim();
  }

  return text;
}

export default getMessagePreview;
